import React, { useCallback, useState } from 'react';

import { Flash } from '@primer/react';

import isNumber from 'lodash/isNumber';

import Button from '../../common/Button/Button';
import CardHeader from '../../common/Labels/CardHeader';
import CardView from '../../common/CardView/CardView';
import LoadingSpinner from '../../common/LoadingSpinner/LoadingSpinner';

import TaskCreationPanel from './TaskCreationPanel';

import { isJSON } from '../../../utils/text_utils';
import useTaskCreation from '../TaskCreationContext/TaskCreationContext';

const ERROR_CARD_STYLE = {
  position: 'absolute', top: 0, width: '80%', margin: '2px'
}

export const TaskCreationModal = ({
  loading = false,
  error = null,
  onCreate = () => { },
  onClose = () => { }
}) => {
  const [taskName, setName] = useState('');
  const [taskType, setTaskType] = useState('run_immediately');
  const [jsonStr, setJSONStr] = useState('');
  const [timeoutVal, setTimeoutVal] = useState('');
  const [schedule, setSchedule] = useState('');

  const hasJSONError = jsonStr?.length && !isJSON(jsonStr);
  const hasTimeoutValError = timeoutVal?.length > 0 && (!isNumber(parseFloat(timeoutVal)) || parseFloat(timeoutVal) < 0);
  const disableNextButton = loading || !taskName?.length || hasJSONError || hasTimeoutValError;

  const handleCreation = useCallback(() => {
    if (onCreate) {
      const isIndefinite = taskType === 'run_continuously';
      const parsedVal = timeoutVal?.length ? parseFloat(timeoutVal) : null;
      onCreate({
        taskName,
        taskType,
        jsonStr,
        timeout: isIndefinite ? undefined : parsedVal,
        schedule: taskType === 'run_immediately' ? undefined : schedule,
      });
    }
  }, [jsonStr, onCreate, schedule, taskName, taskType, timeoutVal]);

  return (
    <CardView className="flex flex-col justify-evenly relative">
      {
        loading && (
          <LoadingSpinner
            className="left-0"
            animationKey="task-creation-loading-spinner"
            useOverlay
          />
        )
      }
      {error && <Flash style={ERROR_CARD_STYLE} variant="danger">{error}</Flash>}
      <div className="flex justify-between items-center mb-2">
        <CardHeader size="2xl">Create Task</CardHeader>
        <Button
          variant="close"
          textColor="border-dark"
          hoverTextColor="border-darker"
          activeTextColor="info"
          onClick={onClose}
        />
      </div>
      <div className="overflow-scroll mt-2" style={{ height: '400px' }}>
        <TaskCreationPanel
          setSchedule={setSchedule}
          jsonStr={jsonStr}
          taskName={taskName}
          taskType={taskType}
          timeoutVal={timeoutVal}
          setName={setName}
          setTaskType={setTaskType}
          setJSONStr={setJSONStr}
          setTimeoutVal={setTimeoutVal}
        />
      </div>
      <div className="w-full flex justify-between pt-4">
        <Button onClick={onClose} size="large">Back</Button>
        <div className="flex justify-end">
          <Button
            className="ml-2"
            variant="primary"
            size="large"
            onClick={handleCreation}
            disabled={disableNextButton}
          >
            Create Task
          </Button>
        </div>
      </div>
    </CardView>
  );
}

// Assumes it's within a TaskCreationContextProvider
const ContextualTaskCreationModal = ({ onClose }) => {
  const { loading, error, onCreate } = useTaskCreation();
  return (
    <TaskCreationModal
      loading={loading}
      error={error}
      onCreate={onCreate}
      onClose={onClose}
    />
  )
}

export default ContextualTaskCreationModal;
